import { useEffect, useState } from 'react';
import useCallApiAndLoad from '../../../hooks/useCallApiAndLoad';
import useAuth from '../../../hooks/useAuth';
import { fetchResponseAdapter } from '../../../adapters/fetchAdapter';
import {
  ClientFeedbackService,
  getVisitClientFeedbackEndpoint,
  getVisitClientFeedbackLinkEndpoint,
} from '../services/dashboardService';

export interface ClientFeedbackLink {
  url: string;
  status: string;
  expires_at: string | null;
}

export interface ClientFeedbackAnswer {
  id: string;
  visit_id: string;
  status: string;
  expires_at: string | null;
  satisfaction: number | null;
  interest: number | null;
  evaluation_options: string[];
  other_text: string | null;
  comment: string | null;
  answered_at: string | null;
}

export type ClientFeedbackState = 'loading' | 'answered' | 'pending' | 'error';

// 404 con este código = el cliente aún no responde.
const CLIENT_FEEDBACK_PENDING_CODE = 1110;

// Respuesta del cliente a una asesoría finalizada. Si aún no responde, se
// consulta el enlace para compartírselo.
const useClientFeedback = (
  visitId?: string,
  enabled = true,
  withLink = true,
  service: ClientFeedbackService = 'pre-sales',
) => {
  const { callEndpoint } = useCallApiAndLoad();
  const { getAuthToken } = useAuth();
  const token = getAuthToken();
  const [state, setState] = useState<ClientFeedbackState>('loading');
  const [answer, setAnswer] = useState<ClientFeedbackAnswer | null>(null);
  const [link, setLink] = useState<ClientFeedbackLink | null>(null);

  useEffect(() => {
    if (!visitId || !enabled) return undefined;
    let active = true;
    setState('loading');
    (async () => {
      const { status, data } = await callEndpoint(
        getVisitClientFeedbackEndpoint(visitId, token, service),
      );
      if (!active) return;
      if (status === 200) {
        setAnswer(fetchResponseAdapter(data).data);
        setState('answered');
        return;
      }
      if (
        status !== 404 ||
        fetchResponseAdapter(data).code !== CLIENT_FEEDBACK_PENDING_CODE
      ) {
        setState('error');
        return;
      }
      if (!withLink) {
        setState('pending');
        return;
      }
      const linkResponse = await callEndpoint(
        getVisitClientFeedbackLinkEndpoint(visitId, token, service),
      );
      if (!active) return;
      if (linkResponse.status === 200) {
        setLink(fetchResponseAdapter(linkResponse.data).data);
        setState('pending');
      } else {
        setState('error');
      }
    })();
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visitId, enabled, service]);

  return { state, answer, link };
};

export default useClientFeedback;
